import { OpenSeaAsset } from "opensea-js/lib/types";
import {
  createContext,
  Dispatch,
  useCallback,
  useContext,
  useReducer,
} from "react";

import { useNftContext } from "@/features/nft/hooks/useNft";
import { getPublicConfig } from "@/helpers/getPublicConfig";

import { getRefetechSingleAssetFunction } from "./getRefetechSingleAssetFunction";

export enum OpenSeaAssetsStatusEnum {
  success = "SUCCESS",
  fail = "FAIL",
  fetching = "FETCHING",
}

type OpenSeaAssetsStateType = {
  [key: string]: {
    status: OpenSeaAssetsStatusEnum;
    asset: OpenSeaAsset | null;
    lastFetchedAt: Date;
  };
};

const OpenSeaAssetsInitialState: OpenSeaAssetsStateType = {};

const OpenSeaAssetsReducer = (
  state: OpenSeaAssetsStateType,
  action: { type: string; payload: any },
) => {
  switch (action.type) {
    case "ADD_ASSET":
      const newOpenSeaAsset: OpenSeaAsset = action.payload;
      if (!newOpenSeaAsset) return { ...state };

      return {
        ...state,
        [newOpenSeaAsset.tokenId]: {
          status: OpenSeaAssetsStatusEnum.success,
          asset: newOpenSeaAsset,
          lastFetchedAt: new Date(),
        },
      };
    case "FETCH_START":
      const startTokenId: string = action.payload;
      return {
        ...state,
        [startTokenId]: {
          status: OpenSeaAssetsStatusEnum.fetching,
          lastFetchedAt: new Date(),
          asset: null,
        },
      };
    case "FETCH_FAIL":
      const failTokenId: string = action.payload;
      return {
        ...state,
        [failTokenId]: {
          status: OpenSeaAssetsStatusEnum.fail,
          lastFetchedAt: new Date(),
          asset: null,
        },
      };
    case "REMOVE_ASSET":
      const tokenId: string = action.payload;
      let updatedState = { ...state };
      delete updatedState[tokenId];
      return updatedState;
    default:
      return state;
  }
};

type refetchAssetType = (tokenId: string, postId: string) => Promise<void>;

// for caching the OpenSeaAssets
const OpenSeaAssetsContext = createContext<
  [OpenSeaAssetsStateType, Dispatch<any>, refetchAssetType]
>([{}, null, null]);

export const MasonryOpenSeaAssetsProvider = (props) => {
  const { seaport } = useNftContext();
  const [openSeaAssetHashmap, dispatch] = useReducer(
    OpenSeaAssetsReducer,
    OpenSeaAssetsInitialState,
  );
  const config = getPublicConfig();

  const refetchSingleAsset = useCallback(
    () =>
      getRefetechSingleAssetFunction({
        tokenAddress: config.service.openSea.contractAddress,
        seaport,
        dispatch,
      }),
    [config, dispatch, seaport],
  );

  return (
    <OpenSeaAssetsContext.Provider
      value={[openSeaAssetHashmap, dispatch, refetchSingleAsset()]}
      {...props}
    />
  );
};

export function useMasonryOpenSeaAssets() {
  return useContext(OpenSeaAssetsContext);
}
